/**
 * DesktopRightPanel — Canonical desktop right-side panel for DashboardLayout.
 * Supports search/filter tab toggle, account view, and arbitrary content slots.
 *
 * Single source of truth: packages/ui
 */
import React from 'react';
import { cn } from '../../lib/utils';
import { X, SlidersHorizontal, Search, User, Sparkles } from 'lucide-react';

export type RightPanelMode = 'none' | 'filters' | 'search' | 'account' | 'agent-c';

export interface DesktopRightPanelProps {
  panel: RightPanelMode;
  onClose: () => void;
  onSwitchPanel?: (mode: RightPanelMode) => void;
  /** Override the default panel title */
  title?: string;
  children?: React.ReactNode;
  className?: string;
}

const PANEL_TITLES: Record<Exclude<RightPanelMode, 'none'>, string> = {
  filters: 'Filters',
  search: 'Search',
  account: 'Account',
  'agent-c': 'Agent C',
};

const PANEL_WIDTH: Record<Exclude<RightPanelMode, 'none'>, { outer: string; inner: string }> = {
  filters: { outer: 'w-[420px] 2xl:w-[480px]', inner: 'w-[420px] 2xl:w-[480px] min-w-[420px] 2xl:min-w-[480px]' },
  search:  { outer: 'w-[420px] 2xl:w-[480px]', inner: 'w-[420px] 2xl:w-[480px] min-w-[420px] 2xl:min-w-[480px]' },
  account: { outer: 'w-[420px] 2xl:w-[480px]', inner: 'w-[420px] 2xl:w-[480px] min-w-[420px] 2xl:min-w-[480px]' },
  'agent-c': { outer: 'w-[420px] 2xl:w-[480px]', inner: 'w-[420px] 2xl:w-[480px] min-w-[420px] 2xl:min-w-[480px]' },
};

const PANEL_ICONS: Record<Exclude<RightPanelMode, 'none'>, React.ReactNode> = {
  filters: <SlidersHorizontal className="w-4 h-4" />,
  search: <Search className="w-4 h-4" />,
  account: <User className="w-4 h-4" />,
  'agent-c': <Sparkles className="w-4 h-4" />,
};

export const DesktopRightPanel: React.FC<DesktopRightPanelProps> = ({
  panel,
  onClose,
  onSwitchPanel,
  title,
  children,
  className,
}) => {
  const isOpen = panel !== 'none';
  const isDiscoveryPanel = panel === 'filters' || panel === 'search';

  return (
    <aside
      className={cn(
        'hidden md:flex flex-col border-l bg-card shrink-0 sticky overflow-hidden z-50',
        'transition-[width,opacity] duration-300 ease-out',
        isOpen
          ? cn(PANEL_WIDTH[panel].outer, 'opacity-100 pointer-events-auto border-border')
          : 'w-0 opacity-0 pointer-events-none border-transparent',
        className,
      )}
      style={{
        top: 'var(--hdr, 56px)',
        height: 'calc(100dvh - var(--hdr, 56px))',
      }}
      data-right-panel="true"
      data-panel-mode={panel}
      data-ui="DesktopRightPanel"
      data-ui-source="@bright-web-skeleton/ui"
      data-ui-version="2026-03-05"
      aria-label={isOpen ? `${title || PANEL_TITLES[panel]} panel` : 'Right panel (closed)'}
      aria-hidden={!isOpen}
    >
      <div className={cn('flex flex-col h-full', isOpen ? PANEL_WIDTH[panel].inner : 'w-0 min-w-0 overflow-hidden')}>
        {/* Panel header */}
        {isOpen && (
          <div className="shrink-0 border-b border-border px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2 text-foreground">
              {!title && PANEL_ICONS[panel]}
              <span className="text-sm font-semibold truncate">{title || PANEL_TITLES[panel]}</span>
            </div>
            <button
              onClick={onClose}
              className="h-8 w-8 flex items-center justify-center rounded-full hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
              aria-label="Close panel"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Search ↔ Filters tab toggle (discovery panels only) */}
        {isOpen && isDiscoveryPanel && onSwitchPanel && (
          <div className="shrink-0 px-4 py-2 border-b border-border">
            <div className="flex bg-muted rounded-lg p-0.5">
              <button
                onClick={() => onSwitchPanel('search')}
                className={cn(
                  'flex-1 px-3 py-1.5 text-xs font-medium rounded-md transition-colors flex items-center justify-center gap-1.5',
                  panel === 'search' ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground',
                )}
              >
                <Search className="w-3 h-3" />
                Search
              </button>
              <button
                onClick={() => onSwitchPanel('filters')}
                className={cn(
                  'flex-1 px-3 py-1.5 text-xs font-medium rounded-md transition-colors flex items-center justify-center gap-1.5',
                  panel === 'filters' ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground',
                )}
              >
                <SlidersHorizontal className="w-3 h-3" />
                Filters
              </button>
            </div>
          </div>
        )}

        {/* Panel body */}
        <div id="desktop-right-panel-body" className="flex-1 overflow-y-auto overflow-x-hidden">
          {isOpen && children}
        </div>
      </div>
    </aside>
  );
};

export default DesktopRightPanel;
